import React, { useEffect, useMemo, useState } from "react";
import {
    Calculator, Download, Loader2, AlertTriangle, TrendingUp, ChevronRight,
    Heart, Droplet, Activity, Brain, Pill, Users, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import api, { API_ROOT, formatApiError } from "@/lib/api";

const CALC_META = {
    framingham: { label: "Framingham CVD 10a", icon: Heart, group: "cardio" },
    ascvd: { label: "ASCVD 10a (AHA/ACC)", icon: Heart, group: "cardio" },
    score2_op: { label: "SCORE2-OP (idoso)", icon: Heart, group: "cardio" },
    ckd_epi: { label: "TFG CKD-EPI 2021", icon: Droplet, group: "renal" },
    cockcroft_gault: { label: "Clearance C-G", icon: Droplet, group: "renal" },
    hba1c: { label: "HbA1c (meta geriátrica)", icon: Droplet, group: "endocrino" },
    ost: { label: "OST — Osteoporose", icon: Activity, group: "ost" },
    charlson: { label: "Charlson (mortalidade)", icon: Users, group: "geral" },
    acb: { label: "Carga anticolinérgica", icon: Pill, group: "meds" },
    sarc_f: { label: "SARC-F (sarcopenia)", icon: Activity, group: "sarco" },
    metabolic_syndrome: { label: "Síndrome metabólica", icon: Activity, group: "metab" },
    cha2ds2_vasc: { label: "CHA₂DS₂-VASc (FA)", icon: Heart, group: "cardio" },
    has_bled: { label: "HAS-BLED (sangramento)", icon: Heart, group: "cardio" },
    barthel: { label: "Barthel (AVDs)", icon: Users, group: "func" },
};

const BAND_COLOR = {
    Alto: "#B33A3A", "Muito Alto": "#8A1A1A",
    Ruim: "#B33A3A", Grave: "#B33A3A", "Alta — considerar deprescrição": "#B33A3A",
    "Alto risco sangramento": "#B33A3A", "Alto risco — encaminhar DEXA imediatamente": "#B33A3A",
    "Dependência total": "#B33A3A", "Dependência grave": "#B33A3A",
    "Provável sarcopenia": "#8A6A15", "Síndrome metabólica": "#8A6A15",
    Intermediário: "#8A6A15", Insuficiente: "#8A6A15", Moderado: "#8A6A15",
    "Risco moderado": "#8A6A15", "Risco moderado — considerar DEXA": "#8A6A15",
    "Moderada — atenção": "#8A6A15", "Aceitável": "#4A6B58",
    Baixo: "#1E3F33", Ótimo: "#1E3F33", "Sem carga": "#1E3F33",
    Independente: "#1E3F33", Baixa: "#1E3F33",
};

function bandColor(band) { return BAND_COLOR[band] || "#4A6B58"; }

export default function ClinicalCalculators({ patientId, consultationId = null }) {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [manualOpen, setManualOpen] = useState(null); // key of expanded calc

    const refresh = () => {
        setLoading(true);
        api.get(`/patients/${patientId}/risk-profile`)
            .then((r) => setProfile(r.data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    };
    useEffect(refresh, [patientId]); // eslint-disable-line

    const downloadPdf = async () => {
        setDownloading(true);
        try {
            const resp = await api.get(`/patients/${patientId}/risk-profile/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `perfil-risco-${patientId}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setDownloading(false); }
    };

    if (loading) {
        return (
            <div className="card-earthy flex items-center gap-2 text-sm text-muted-clinical">
                <Loader2 className="animate-spin" size={14} /> Calculando escalas...
            </div>
        );
    }
    if (!profile) return null;

    const results = profile.results || {};
    const inputsUsed = profile.inputs_used || {};
    const nonComputable = Object.keys(CALC_META).filter((k) => !results[k]);

    return (
        <div className="space-y-4" data-testid="clinical-calculators">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h2 className="font-display text-xl text-brand flex items-center gap-2">
                        <Calculator size={20} /> Calculadoras & Perfil de Risco
                    </h2>
                    <p className="text-xs text-muted-clinical">
                        Calculado automaticamente a partir da AGA + exames + medicações. Idade: {profile.patient?.age || "—"} anos.
                    </p>
                </div>
                <button onClick={downloadPdf} className="btn-primary text-sm" disabled={downloading} data-testid="risk-profile-download-pdf">
                    {downloading ? <Loader2 className="animate-spin" size={14} /> : <Download size={14} />} Relatório PDF
                </button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {Object.entries(results).map(([key, r]) => (
                    <CalcCard key={key} calcKey={key} result={r} onManual={() => setManualOpen(key)} />
                ))}
            </div>

            {nonComputable.length > 0 && (
                <div className="card-earthy bg-alt/40" data-testid="calc-manual-section">
                    <div className="flex items-center gap-2 mb-2 text-brand-secondary text-xs uppercase tracking-widest font-semibold">
                        <AlertTriangle size={12} /> Requerem preenchimento manual
                    </div>
                    <p className="text-xs text-muted-clinical mb-3">
                        Sem dados suficientes na AGA/exames para computar automaticamente. Clique para abrir o formulário.
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {nonComputable.map((k) => {
                            const M = CALC_META[k];
                            const Icon = M.icon;
                            return (
                                <button
                                    key={k}
                                    onClick={() => setManualOpen(k)}
                                    className="text-xs px-3 py-1.5 rounded-full bg-alt hover:bg-brand hover:text-white transition-colors flex items-center gap-1"
                                    data-testid={`calc-open-manual-${k}`}
                                >
                                    <Icon size={11} /> {M.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="card-earthy bg-alt/30 text-xs text-muted-clinical">
                <div className="font-semibold text-brand-secondary uppercase tracking-widest mb-2">
                    Dados utilizados no cálculo
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1">
                    {Object.entries(inputsUsed).map(([k, v]) => (
                        v == null || v === false ? null : (
                            <div key={k}>
                                <span className="text-brand-secondary">{k}:</span>{" "}
                                <strong>{typeof v === "boolean" ? "Sim" : v}</strong>
                            </div>
                        )
                    ))}
                </div>
            </div>

            {manualOpen && (
                <ManualCalcDialog
                    calcKey={manualOpen}
                    initialInputs={inputsUsed}
                    patient={profile.patient}
                    patientId={patientId}
                    consultationId={consultationId}
                    onClose={() => setManualOpen(null)}
                />
            )}
        </div>
    );
}

function CalcCard({ calcKey, result, onManual }) {
    const M = CALC_META[calcKey];
    const Icon = M?.icon || Calculator;
    if (result.error) {
        return (
            <div className="card-earthy border-l-4 border-[var(--border-organic)]" data-testid={`calc-card-${calcKey}`}>
                <h3 className="font-medium text-brand text-sm flex items-center gap-2">
                    <Icon size={14} /> {M?.label || calcKey}
                </h3>
                <p className="text-xs text-muted-clinical italic mt-2">{result.error}</p>
                <button onClick={onManual} className="text-xs text-brand hover:underline mt-2" data-testid={`calc-manual-${calcKey}`}>
                    Preencher manualmente <ChevronRight size={11} className="inline" />
                </button>
            </div>
        );
    }
    return (
        <div
            className="card-earthy border-l-4"
            style={{ borderLeftColor: bandColor(result.band) }}
            data-testid={`calc-card-${calcKey}`}
        >
            <h3 className="font-medium text-brand text-sm flex items-center gap-2">
                <Icon size={14} /> {M?.label || calcKey}
            </h3>
            <div className="flex items-baseline gap-2 mt-2">
                <span className="font-display text-2xl font-medium" style={{ color: bandColor(result.band) }}>
                    {result.value}
                </span>
                <span className="text-xs text-muted-clinical">{result.unit}</span>
                {result.band && (
                    <span
                        className="ml-auto text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full"
                        style={{ background: bandColor(result.band) + "20", color: bandColor(result.band) }}
                    >
                        {result.band}
                    </span>
                )}
            </div>
            {result.target && (
                <p className="text-xs text-brand-secondary mt-1">
                    Meta: <strong>{result.target}</strong>
                </p>
            )}
            {result.estimated_avg_glucose && (
                <p className="text-xs text-brand-secondary">
                    Glicemia média estimada: <strong>{result.estimated_avg_glucose} mg/dL</strong>
                </p>
            )}
            {result.estimated_10y_mortality_pct && (
                <p className="text-xs text-brand-secondary">
                    Mortalidade estimada 10a: <strong>{result.estimated_10y_mortality_pct}%</strong>
                </p>
            )}
            <p className="text-xs text-muted-clinical mt-2 line-clamp-2">{result.interpretation}</p>
            {result.reference && (
                <p className="text-[10px] text-muted-clinical/70 mt-1 italic">{result.reference}</p>
            )}
            <button onClick={onManual} className="text-xs text-brand hover:underline mt-2" data-testid={`calc-manual-${calcKey}`}>
                Editar entradas <ChevronRight size={11} className="inline" />
            </button>
        </div>
    );
}

// ---------------------- Manual calculator dialog ----------------------
// `discrete` = array of valid values → renderiza como segmented control (chips) em vez de input numérico livre
const MANUAL_FIELDS = {
    framingham: [
        { key: "age", label: "Idade", type: "number", auto: "age" },
        { key: "sex", label: "Sexo", type: "select", options: [["M", "Masculino"], ["F", "Feminino"]], auto: "sex" },
        { key: "total_cholesterol", label: "Colesterol total", suffix: "mg/dL", type: "number" },
        { key: "hdl", label: "HDL", suffix: "mg/dL", type: "number" },
        { key: "systolic_bp", label: "PA sistólica", suffix: "mmHg", type: "number" },
        { key: "treated_htn", label: "HAS em tratamento?", type: "bool" },
        { key: "smoker", label: "Tabagista atual?", type: "bool" },
        { key: "diabetes", label: "Diabetes?", type: "bool" },
    ],
    ascvd: [
        { key: "age", label: "Idade", hint: "40-79", type: "number", auto: "age" },
        { key: "sex", label: "Sexo", type: "select", options: [["M", "Masculino"], ["F", "Feminino"]], auto: "sex" },
        { key: "race", label: "Raça", type: "select", options: [["white_other", "Branco/outro"], ["black", "Afroamericano"]] },
        { key: "total_cholesterol", label: "Colesterol total", suffix: "mg/dL", type: "number" },
        { key: "hdl", label: "HDL", suffix: "mg/dL", type: "number" },
        { key: "systolic_bp", label: "PA sistólica", suffix: "mmHg", type: "number" },
        { key: "treated_htn", label: "HAS em tratamento?", type: "bool" },
        { key: "smoker", label: "Tabagista?", type: "bool" },
        { key: "diabetes", label: "Diabetes?", type: "bool" },
    ],
    score2_op: [
        { key: "age", label: "Idade", hint: "65-89", type: "number", auto: "age" },
        { key: "sex", label: "Sexo", type: "select", options: [["M", "Masculino"], ["F", "Feminino"]], auto: "sex" },
        { key: "total_cholesterol_mmol", label: "Colesterol total", suffix: "mmol/L", hint: "divida mg/dL por 38,67", type: "number", step: "0.01" },
        { key: "hdl_mmol", label: "HDL", suffix: "mmol/L", type: "number", step: "0.01" },
        { key: "systolic_bp", label: "PA sistólica", suffix: "mmHg", type: "number" },
        { key: "smoker", label: "Tabagista?", type: "bool" },
        { key: "region", label: "Risco regional (Brasil ~ moderado)", type: "select",
          options: [["low", "Baixo"], ["moderate", "Moderado"], ["high", "Alto"], ["very_high", "Muito alto"]] },
    ],
    ckd_epi: [
        { key: "creatinine_mg_dl", label: "Creatinina", suffix: "mg/dL", type: "number", step: "0.01" },
        { key: "age", label: "Idade", type: "number", auto: "age" },
        { key: "sex", label: "Sexo", type: "select", options: [["M", "Masculino"], ["F", "Feminino"]], auto: "sex" },
    ],
    cockcroft_gault: [
        { key: "creatinine_mg_dl", label: "Creatinina", suffix: "mg/dL", type: "number", step: "0.01" },
        { key: "age", label: "Idade", type: "number", auto: "age" },
        { key: "sex", label: "Sexo", type: "select", options: [["M", "Masculino"], ["F", "Feminino"]], auto: "sex" },
        { key: "weight_kg", label: "Peso", suffix: "kg", type: "number", step: "0.1" },
    ],
    hba1c: [
        { key: "hba1c", label: "HbA1c", suffix: "%", type: "number", step: "0.1" },
        { key: "cfs", label: "CFS (opcional)", hint: "Clinical Frailty Scale", type: "number" },
        { key: "fried", label: "Fried (opcional)", hint: "0-5", type: "number" },
    ],
    ost: [
        { key: "age", label: "Idade", type: "number", auto: "age" },
        { key: "weight_kg", label: "Peso", suffix: "kg", type: "number", step: "0.1" },
        { key: "sex", label: "Sexo", type: "select", options: [["M", "Masculino"], ["F", "Feminino"]], auto: "sex" },
    ],
    sarc_f: [
        { key: "strength", label: "Força", hint: "dificuldade em carregar 4-5 kg", type: "discrete", options: [[0, "Nenhuma"], [1, "Alguma"], [2, "Muita / incapaz"]] },
        { key: "walking", label: "Marcha", hint: "auxílio p/ atravessar cômodo", type: "discrete", options: [[0, "Nenhuma"], [1, "Alguma"], [2, "Muita / incapaz"]] },
        { key: "rising", label: "Levantar da cadeira", type: "discrete", options: [[0, "Sem ajuda"], [1, "Com ajuda"], [2, "Não consegue"]] },
        { key: "stairs", label: "Subir 10 degraus", type: "discrete", options: [[0, "Sem dificuldade"], [1, "Alguma"], [2, "Muita / incapaz"]] },
        { key: "falls", label: "Quedas no último ano", type: "discrete", options: [[0, "Nenhuma"], [1, "1 a 3"], [2, "≥ 4"]] },
    ],
    barthel: [
        { key: "alimentacao", label: "Alimentação", type: "discrete", options: [[0, "Dependente"], [5, "Precisa ajuda"], [10, "Independente"]] },
        { key: "banho", label: "Banho", type: "discrete", options: [[0, "Dependente"], [5, "Independente"]] },
        { key: "higiene_pessoal", label: "Higiene pessoal", type: "discrete", options: [[0, "Dependente"], [5, "Independente"]] },
        { key: "vestir", label: "Vestir-se", type: "discrete", options: [[0, "Dependente"], [5, "Precisa ajuda"], [10, "Independente"]] },
        { key: "controle_intestinal", label: "Controle intestinal", type: "discrete", options: [[0, "Incontinente"], [5, "Ocasional"], [10, "Continente"]] },
        { key: "controle_urinario", label: "Controle urinário", type: "discrete", options: [[0, "Incontinente"], [5, "Ocasional"], [10, "Continente"]] },
        { key: "uso_banheiro", label: "Uso do banheiro", type: "discrete", options: [[0, "Dependente"], [5, "Precisa ajuda"], [10, "Independente"]] },
        { key: "transferencia", label: "Transferência cama-cadeira", type: "discrete", options: [[0, "Incapaz"], [5, "Ajuda maior"], [10, "Ajuda mínima"], [15, "Independente"]] },
        { key: "deambulacao", label: "Deambulação", type: "discrete", options: [[0, "Imobilizado"], [5, "Cadeirante"], [10, "Ajuda"], [15, "Independente"]] },
        { key: "escadas", label: "Escadas", type: "discrete", options: [[0, "Incapaz"], [5, "Precisa ajuda"], [10, "Independente"]] },
    ],
    cha2ds2_vasc: [
        { key: "age", label: "Idade", type: "number", auto: "age" },
        { key: "sex", label: "Sexo", type: "select", options: [["M", "Masculino"], ["F", "Feminino"]], auto: "sex" },
        { key: "chf", label: "Insuficiência cardíaca?", type: "bool" },
        { key: "hypertension", label: "HAS?", type: "bool" },
        { key: "diabetes", label: "Diabetes?", type: "bool" },
        { key: "stroke_tia", label: "AVC / AIT prévio?", type: "bool" },
        { key: "vascular_disease", label: "Doença vascular?", type: "bool" },
    ],
    has_bled: [
        { key: "hypertension_uncontrolled", label: "HAS não controlada?", type: "bool" },
        { key: "renal", label: "Função renal anormal?", type: "bool" },
        { key: "liver", label: "Função hepática anormal?", type: "bool" },
        { key: "stroke", label: "AVC prévio?", type: "bool" },
        { key: "bleeding_history", label: "Sangramento prévio?", type: "bool" },
        { key: "labile_inr", label: "INR lábil?", type: "bool" },
        { key: "age_65plus", label: "Idade ≥ 65?", type: "bool" },
        { key: "drugs_alcohol", label: "Drogas / álcool", type: "discrete", options: [[0, "Não"], [1, "1 fator"], [2, "Ambos"]] },
    ],
    charlson: [
        { key: "age", label: "Idade", type: "number", auto: "age" },
        { key: "conditions.iam", label: "IAM prévio", type: "bool" },
        { key: "conditions.icc", label: "ICC", type: "bool" },
        { key: "conditions.vasc_perif", label: "Doença vascular periférica", type: "bool" },
        { key: "conditions.avc", label: "AVC / doença cerebrovascular", type: "bool" },
        { key: "conditions.demencia", label: "Demência", type: "bool" },
        { key: "conditions.dpoc", label: "DPOC", type: "bool" },
        { key: "conditions.hepatopatia_leve", label: "Hepatopatia leve", type: "bool" },
        { key: "conditions.diabetes_sem_lesao", label: "DM sem lesão de órgão", type: "bool" },
        { key: "conditions.diabetes_com_lesao", label: "DM com lesão de órgão", type: "bool" },
        { key: "conditions.doenca_renal_moderada_grave", label: "DRC moderada/grave", type: "bool" },
        { key: "conditions.tumor_solido", label: "Tumor sólido s/ metástase", type: "bool" },
        { key: "conditions.tumor_metastatico", label: "Tumor metastático", type: "bool" },
    ],
    acb: [
        { key: "medications_note", label: "Sem edição manual — puxa da AGA. Adicione medicações na aba Medicamentos.", type: "info" },
    ],
    metabolic_syndrome: [
        { key: "weight_kg", label: "Peso", suffix: "kg", type: "number", step: "0.1" },
        { key: "height_cm", label: "Altura", suffix: "cm", type: "number" },
        { key: "waist_cm", label: "Cintura", suffix: "cm", type: "number" },
        { key: "sex", label: "Sexo", type: "select", options: [["M", "Masculino"], ["F", "Feminino"]], auto: "sex" },
        { key: "triglycerides", label: "Triglicérides", suffix: "mg/dL", type: "number" },
        { key: "hdl", label: "HDL", suffix: "mg/dL", type: "number" },
        { key: "systolic_bp", label: "PAS", suffix: "mmHg", type: "number" },
        { key: "diastolic_bp", label: "PAD", suffix: "mmHg", type: "number" },
        { key: "fasting_glucose", label: "Glicemia jejum", suffix: "mg/dL", type: "number" },
        { key: "on_htn_rx", label: "Usa anti-hipertensivo?", type: "bool" },
        { key: "on_dm_rx", label: "Usa antidiabético?", type: "bool" },
        { key: "on_lipid_rx", label: "Usa hipolipemiante?", type: "bool" },
    ],
};

function ManualCalcDialog({ calcKey, initialInputs, patient, patientId, consultationId, onClose }) {
    const fields = MANUAL_FIELDS[calcKey] || [];
    const [values, setValues] = useState(() => {
        const seed = {};
        for (const f of fields) {
            const parts = f.key.split(".");
            if (parts.length === 2) {
                seed[f.key] = false;
            } else if (f.type === "bool") {
                seed[f.key] = initialInputs?.[f.key] === true;
            } else if (f.type === "discrete") {
                seed[f.key] = null;
            } else if (f.type === "select") {
                if (f.auto === "sex" && patient?.sex) seed[f.key] = patient.sex;
                else seed[f.key] = initialInputs?.[f.key] || f.options?.[0]?.[0];
            } else if (f.auto === "age") {
                seed[f.key] = patient?.age || initialInputs?.age || "";
            } else if (f.auto === "sex") {
                seed[f.key] = patient?.sex || "M";
            } else {
                seed[f.key] = initialInputs?.[f.key] ?? "";
            }
        }
        return seed;
    });
    const [result, setResult] = useState(null);
    const [computing, setComputing] = useState(false);
    const [savedInputs, setSavedInputs] = useState(null);
    const [saving, setSaving] = useState(false);
    const [savedTs, setSavedTs] = useState(null);

    const saveHistory = async () => {
        if (!result || result.error) return;
        setSaving(true);
        try {
            await api.post(`/patients/${patientId}/calculator-events`, {
                calculator: calcKey,
                inputs: savedInputs || {},
                result,
                consultation_id: consultationId,
            });
            setSavedTs(new Date().toISOString());
            toast.success("Cálculo salvo no histórico do paciente (aparece na Linha do Tempo).");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const submit = async () => {
        setComputing(true);
        try {
            const inputs = {};
            for (const f of fields) {
                if (f.type === "info") continue;
                const v = values[f.key];
                const parts = f.key.split(".");
                if (parts.length === 2) {
                    if (!inputs[parts[0]]) inputs[parts[0]] = {};
                    inputs[parts[0]][parts[1]] = !!v;
                } else if (f.type === "number" || f.type === "discrete") {
                    inputs[f.key] = v === "" || v == null ? null : Number(v);
                } else if (f.type === "bool") {
                    inputs[f.key] = !!v;
                } else {
                    inputs[f.key] = v;
                }
            }
            const { data } = await api.post(`/calculators/${calcKey}`, { inputs });
            setResult(data.result);
            setSavedInputs(inputs);
            setSavedTs(null);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setComputing(false); }
    };

    const clearAll = () => {
        const cleared = {};
        for (const f of fields) {
            if (f.type === "bool") cleared[f.key] = false;
            else if (f.type === "discrete") cleared[f.key] = null;
            else if (f.type === "select") cleared[f.key] = f.options?.[0]?.[0];
            else cleared[f.key] = "";
        }
        setValues(cleared);
        setResult(null);
    };

    return (
        <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            data-testid={`calc-dialog-${calcKey}`}
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col border-2 border-[#A08A5C]/40">
                {/* Header */}
                <div className="p-5 border-b-2 border-[#EDE0C6] flex items-center justify-between bg-gradient-to-r from-[#FBF7EF] to-white rounded-t-2xl">
                    <div>
                        <p className="text-[10px] uppercase tracking-widest text-[#8A6A15] font-semibold mb-0.5">
                            Calculadora clínica
                        </p>
                        <h3 className="font-display text-xl text-brand font-medium">
                            {CALC_META[calcKey]?.label || calcKey}
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-9 h-9 rounded-full bg-alt hover:bg-brand hover:text-white flex items-center justify-center transition-colors"
                        aria-label="Fechar"
                        data-testid="calc-dialog-close"
                    >
                        ✕
                    </button>
                </div>

                {/* Body — scroll-friendly, 2-col grid where possible */}
                <div className="flex-1 overflow-y-auto p-5 bg-white">
                    <div className={fields.length > 4 ? "grid md:grid-cols-2 gap-x-6 gap-y-4" : "space-y-4"}>
                        {fields.map((f) => (
                            <FieldRow
                                key={f.key}
                                field={f}
                                value={values[f.key]}
                                onChange={(nv) => setValues((v) => ({ ...v, [f.key]: nv }))}
                            />
                        ))}
                    </div>

                    {result && (
                        <div
                            className="mt-6 rounded-xl overflow-hidden shadow-md"
                            data-testid={`calc-result-${calcKey}`}
                            style={{ borderLeft: `6px solid ${bandColor(result.band)}` }}
                        >
                            <div className="p-4 bg-gradient-to-br from-[#FBF7EF] to-white">
                                {result.error ? (
                                    <p className="text-sm text-red-700 font-medium">{result.error}</p>
                                ) : (
                                    <>
                                        <div className="flex items-baseline gap-3 flex-wrap">
                                            <span className="font-display text-4xl font-medium" style={{ color: bandColor(result.band) }}>
                                                {result.value}
                                            </span>
                                            <span className="text-sm text-muted-clinical">{result.unit}</span>
                                            {result.band && (
                                                <span
                                                    className="text-xs uppercase tracking-widest font-semibold px-3 py-1 rounded-full"
                                                    style={{ background: bandColor(result.band), color: "#FFF" }}
                                                >
                                                    {result.band}
                                                </span>
                                            )}
                                        </div>
                                        {result.target && (
                                            <p className="text-sm text-brand-secondary mt-2">
                                                Meta: <strong>{result.target}</strong>
                                                {result.estimated_avg_glucose && <>  ·  eAG: <strong>{result.estimated_avg_glucose} mg/dL</strong></>}
                                            </p>
                                        )}
                                        {result.estimated_10y_mortality_pct && (
                                            <p className="text-sm text-brand-secondary mt-1">
                                                Mortalidade estimada 10a: <strong>{result.estimated_10y_mortality_pct}%</strong>
                                            </p>
                                        )}
                                        <p className="text-sm text-brand mt-3 leading-relaxed">{result.interpretation}</p>
                                        {result.reference && (
                                            <p className="text-[11px] italic text-muted-clinical mt-3 pt-3 border-t border-[#EDE0C6]">
                                                {result.reference}
                                            </p>
                                        )}
                                        {patientId && (
                                            <div className="mt-3 pt-3 border-t border-[#EDE0C6] flex items-center justify-between gap-2 flex-wrap">
                                                <span className="text-[11px] text-muted-clinical">
                                                    {savedTs ? `✓ Salvo às ${new Date(savedTs).toLocaleTimeString('pt-BR')} — aparece na Linha do Tempo` : "Salve este cálculo para acompanhar a evolução ao longo das consultas."}
                                                </span>
                                                <button
                                                    onClick={saveHistory}
                                                    disabled={saving || !!savedTs}
                                                    className="btn-secondary text-xs"
                                                    data-testid={`calc-save-history-${calcKey}`}
                                                >
                                                    {saving ? <Loader2 className="animate-spin" size={12} /> : savedTs ? <span>✓</span> : <Sparkles size={12} />}
                                                    {savedTs ? "Salvo no histórico" : "Salvar no histórico"}
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer sticky */}
                <div className="p-4 border-t-2 border-[#EDE0C6] flex justify-between gap-2 bg-white rounded-b-2xl">
                    <button onClick={clearAll} className="btn-ghost text-sm" data-testid="calc-clear">
                        Limpar campos
                    </button>
                    <div className="flex gap-2">
                        <button onClick={onClose} className="btn-ghost text-sm">Fechar</button>
                        <button
                            onClick={submit}
                            disabled={computing || fields.every((f) => f.type === "info")}
                            className="btn-primary"
                            data-testid={`calc-submit-${calcKey}`}
                        >
                            {computing ? <Loader2 className="animate-spin" size={16} /> : <Calculator size={16} />}
                            Calcular
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function FieldRow({ field: f, value, onChange }) {
    if (f.type === "info") {
        return (
            <div className="rounded-lg bg-[#FBF4E5] border border-[#D4A574] p-3">
                <p className="text-sm text-brand-secondary">{f.label}</p>
            </div>
        );
    }
    return (
        <div>
            <label className="block text-sm font-semibold text-brand mb-1.5">
                {f.label}
                {f.suffix && <span className="text-muted-clinical font-normal ml-1">({f.suffix})</span>}
            </label>
            {f.hint && <p className="text-[11px] text-muted-clinical -mt-1 mb-1.5">{f.hint}</p>}
            {f.type === "bool" ? (
                <BoolToggle value={value} onChange={onChange} testid={`calc-input-${f.key}`} />
            ) : f.type === "discrete" ? (
                <SegmentedControl options={f.options} value={value} onChange={onChange} testid={`calc-input-${f.key}`} />
            ) : f.type === "select" ? (
                <select
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="input-strong"
                    data-testid={`calc-input-${f.key}`}
                >
                    {f.options.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
                </select>
            ) : (
                <input
                    type="number"
                    step={f.step || "any"}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="input-strong"
                    inputMode="decimal"
                    data-testid={`calc-input-${f.key}`}
                    placeholder="—"
                />
            )}
        </div>
    );
}

function BoolToggle({ value, onChange, testid }) {
    return (
        <div className="inline-flex rounded-lg overflow-hidden border-2 border-[#A08A5C]" data-testid={testid}>
            <button
                type="button"
                onClick={() => onChange(false)}
                className={`px-4 py-1.5 text-sm font-medium transition-colors ${!value ? "bg-brand text-white" : "bg-white text-brand hover:bg-alt"}`}
            >
                Não
            </button>
            <button
                type="button"
                onClick={() => onChange(true)}
                className={`px-4 py-1.5 text-sm font-medium transition-colors ${value ? "bg-brand text-white" : "bg-white text-brand hover:bg-alt"}`}
            >
                Sim
            </button>
        </div>
    );
}

function SegmentedControl({ options, value, onChange, testid }) {
    return (
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-1.5" data-testid={testid}>
            {options.map(([val, lbl]) => {
                const active = value === val;
                return (
                    <button
                        type="button"
                        key={val}
                        onClick={() => onChange(val)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium border-2 transition-colors text-left ${active ? "bg-brand text-white border-brand" : "bg-white text-brand border-[#A08A5C]/50 hover:border-brand hover:bg-alt"}`}
                    >
                        <span className="text-xs opacity-70 mr-1.5">{val}</span>
                        {lbl}
                    </button>
                );
            })}
        </div>
    );
}
